<template>
  <q-select
    :model-value="value"
    :options="filterOptions"
    :error="error"
    :error-message="errorMessage"
    :label="label"
    :virtual-scroll-slice-size="5"
    stack-label
    hide-bottom-space
    outlined
    use-input
    dense
    clearable
    @clear="onClear"
    @filter="onSelectFilter"
    @update:model-value="(val) => $emit('update:modelValue', val)"
  >
    <!-- Selected state/country -->
    <template v-if="value" #selected-item="scope">
      <q-item-label>
        <span v-if="value.state">{{ value.state.name }}, </span>
        <span v-if="value.country">{{ value.country.name }}</span>
      </q-item-label>
    </template>
    <template #option="scope">
      <q-list separator>
        <!-- Country object -->
        <q-item
          :key="'country-' + scope.opt.id"
          v-close-popup
          class="text-bold"
          style="font-size: 1.1em;"
          :clickable="mode !== 'state'"
          :active="country === scope.opt.id"
          @click="setCountry(scope.opt.id)"
        >
          <q-item-section>
            <q-item-label>
              {{ scope.opt.name }}
            </q-item-label>
          </q-item-section>
        </q-item>
        <!-- List each of the states for this country -->
        <q-list
          v-if="mode === 'state'"
        >
          <q-item
            v-for="child in scope.opt.states"
            :key="'state-' + child.id"
            v-close-popup
            clickable
            :inset-level="0.5"
            :active="state === child.id"
            @click="setState(child.id, scope.opt.id)"
          >
            <q-item-section>
              <q-item-label>
                {{ child.name }}
              </q-item-label>
            </q-item-section>
          </q-item>
        </q-list>
      </q-list>
    </template>
  </q-select>
</template>

<script>
/**
 * CountryPicker allows you to pick a country and/or state
 */
export default {
  props: {
    // Selected country ID
    country: {
      type: Number,
      default: null
    },
    // Selected state ID
    state: {
      type: Number,
      default: null
    },
    // Is error
    error: {
      type: Boolean,
      default: false
    },
    // Error message if applicable
    errorMessage: {
      type: String,
      default: undefined
    },
    // Label to display
    label: {
      type: String,
      default: undefined
    },
    // Mode: state, country (default)
    mode: {
      type: String
    }
  },
  emits: ['update:modelValue', 'setCountry', 'setState'],
  data () {
    return {
      filterOptions: []
    }
  },
  computed: {
    // The selected state/country option
    value () {
      if (this.mode === 'state') {
        if (!this.state) {
          return null
        }

        for (const country of this.options) {
          for (const state of country.states) {
            if (state.id === this.state) {
              return { state, country }
            }
          }
        }
        return null
      }

      if (!this.country) {
        return null
      }

      for (const country of this.options) {
        if (country.id === this.country) {
          return {
            country,
            state: null
          }
        }
      }

      return null
    },
    options () {
      return this.$store.getters.getSystemSetting('countries')
    }
  },
  methods: {
    // Filter function which will filter by either state or country, depending on the selected mode
    onSelectFilter (val, update) {
      update(() => {
        if (val === '') {
          this.filterOptions = this.options
        }
        else {
          const needle = val.toLowerCase()
          if (this.mode === 'state') {
            const regenerated = []
            for (const country of this.options) {
              const countryNameMatches = country.name.toLowerCase().includes(needle)
              const validStates = country.states.filter(
                state => state.name.toLowerCase().includes(needle)
              )
              if (countryNameMatches || validStates.length > 0) {
                regenerated.push({
                  ...country,
                  states: countryNameMatches ? country.states : validStates
                })
              }
            }
            this.filterOptions = regenerated
          }
          else {
            this.filterOptions = this.options.filter(country => country.name.toLowerCase().includes(needle))
          }
        }
      })
    },
    // Setting country / state by ID
    setCountry (countryId) {
      this.$emit('setCountry', countryId)
    },
    setState (stateId, countryId) {
      this.$emit('setState', stateId)
      this.setCountry(countryId)
    },
    onClear () {
      if (this.mode === 'state') {
        this.setState(null, null)
      }
      else {
        this.setCountry(null)
      }
    }
  }
}
</script>
